import { z } from "zod";

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max, "Texte trop long")
    .nullish()
    .transform((v) => (v ? v : null));

export const listSubcontractorsInput = z.object({
  confirmationId: z.string().optional(),
  includeArchived: z.boolean().default(false),
});

export const saveSubcontractorInput = z.object({
  confirmationId: z.string().optional(),
  /** Absent à la création. */
  subcontractorId: z.string().uuid().optional(),
  name: z.string().trim().min(1, "La raison sociale est requise").max(120, "Nom trop long"),
  trade: optional(80),
  siret: z
    .string()
    .transform((v) => v.replace(/\s/g, ""))
    .pipe(z.string().regex(/^\d{14}$/, "Le SIRET doit faire 14 chiffres"))
    .nullish()
    .or(z.literal("").transform(() => null)),
  contactName: optional(120),
  phone: optional(30),
  email: z
    .string()
    .trim()
    .email("Adresse e-mail invalide")
    .max(200)
    .nullish()
    .or(z.literal("").transform(() => null)),
  address: optional(300),
  legalForm: optional(80),
  registration: optional(120),
  signatoryName: optional(120),
  signatoryRole: optional(80),
  isSme: z.boolean().nullish(),
  bankName: optional(120),
  /** Espaces tolérés à la saisie, retirés à l'enregistrement. */
  iban: z
    .string()
    .transform((v) => v.replace(/\s/g, "").toUpperCase())
    .refine((v) => v === "" || /^[A-Z]{2}\d{2}[0-9A-Z]{11,30}$/.test(v), "IBAN invalide : deux lettres, deux chiffres, puis le numéro")
    .transform((v) => (v === "" ? null : v))
    .nullish(),
  notes: optional(1000),
});

export const archiveSubcontractorInput = z.object({
  confirmationId: z.string().optional(),
  subcontractorId: z.string().uuid("Identifiant de sous-traitant attendu"),
  /** Faux pour ressortir une fiche du carnet archivé. */
  archived: z.boolean().default(true),
});

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Date attendue au format AAAA-MM-JJ")
  .nullish()
  .or(z.literal("").transform(() => null));

const uuidOrNull = z
  .string()
  .uuid()
  .nullish()
  .or(z.literal("").transform(() => null));

export const saveSubcontractInput = z.object({
  confirmationId: z.string().optional(),
  /** Absent à la création. */
  subcontractId: z.string().uuid().optional(),
  projectId: z.string().uuid("Identifiant de chantier attendu"),
  subcontractorId: z.string().uuid("Choisissez un sous-traitant"),
  lotId: uuidOrNull,
  phaseId: uuidOrNull,
  scope: optional(300),
  amountHt: z.coerce
    .number({ invalid_type_error: "Montant attendu" })
    .min(0, "Le montant ne peut pas être négatif")
    .max(99_999_999, "Montant trop élevé"),
  status: z.enum(["draft", "signed", "completed", "cancelled"]).default("draft"),
  signedOn: isoDate,
  approvalStatus: z.enum(["not_presented", "presented", "approved", "refused"]).default("not_presented"),
  presentedOn: isoDate,
  approvalDecidedOn: isoDate,
  paymentGuarantee: z.enum(["none", "caution", "delegation", "direct"]).default("none"),
  publicContractRef: optional(300),
  priceRevision: optional(500),
  advanceRequested: z.boolean().default(false),
  notes: optional(1000),
});

export const removeSubcontractInput = z.object({
  confirmationId: z.string().optional(),
  subcontractId: z.string().uuid("Identifiant de marché attendu"),
});

export type ListSubcontractorsInput = z.infer<typeof listSubcontractorsInput>;
export type SaveSubcontractorInput = z.infer<typeof saveSubcontractorInput>;
export type SaveSubcontractorFormInput = z.input<typeof saveSubcontractorInput>;
export type ArchiveSubcontractorInput = z.infer<typeof archiveSubcontractorInput>;
export type SaveSubcontractInput = z.infer<typeof saveSubcontractInput>;
export type SaveSubcontractFormInput = z.input<typeof saveSubcontractInput>;
export type RemoveSubcontractInput = z.infer<typeof removeSubcontractInput>;

const kinds = ["urssaf", "kbis", "decennale", "rc_pro", "salaries_etrangers", "autre"] as const;

export const addSubcontractorDocumentInput = z.object({
  confirmationId: z.string().optional(),
  subcontractorId: z.string().uuid("Identifiant de sous-traitant attendu"),
  kind: z.enum(kinds),
  label: optional(120),
  issuedOn: isoDate,
  expiresOn: isoDate,
  /** Le fichier, déjà déposé dans le bucket `documents`. Facultatif. */
  storagePath: z.string().min(1).max(500).nullish(),
  fileName: z.string().trim().min(1).max(200).nullish(),
  mimeType: z.string().max(120).nullish(),
  sizeBytes: z.number().int().positive().nullish(),
  notes: optional(1000),
});

export const updateSubcontractorDocumentInput = z.object({
  confirmationId: z.string().optional(),
  documentId: z.string().uuid("Identifiant de pièce attendu"),
  label: optional(120),
  issuedOn: isoDate,
  expiresOn: isoDate,
  notes: optional(1000),
});

export const removeSubcontractorDocumentInput = z.object({
  confirmationId: z.string().optional(),
  documentId: z.string().uuid("Identifiant de pièce attendu"),
});

export type AddSubcontractorDocumentInput = z.infer<typeof addSubcontractorDocumentInput>;
export type UpdateSubcontractorDocumentInput = z.infer<typeof updateSubcontractorDocumentInput>;
export type RemoveSubcontractorDocumentInput = z.infer<typeof removeSubcontractorDocumentInput>;

const money = (positive: string) =>
  z.coerce
    .number({ invalid_type_error: "Montant attendu" })
    .positive(positive)
    .max(99_999_999, "Montant trop élevé");

export const saveSubcontractInvoiceInput = z.object({
  confirmationId: z.string().optional(),
  /** Absent à la création. */
  invoiceId: z.string().uuid().optional(),
  subcontractId: z.string().uuid("Identifiant de marché attendu"),
  invoiceNumber: z.string().trim().min(1, "Le numéro de la facture est requis").max(60, "Numéro trop long"),
  invoiceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "La date de la facture est requise"),
  dueDate: isoDate,
  amountHt: money("Le montant HT doit être positif"),
  vatMode: z.enum(["autoliquidation", "normale", "franchise"]).default("autoliquidation"),
  vatRate: z.coerce.number().refine((v) => [0, 2.1, 5.5, 10, 20].includes(v), "Taux de TVA inconnu").default(20),
  retentionPercent: z.coerce
    .number({ invalid_type_error: "Pourcentage attendu" })
    .min(0, "La retenue ne peut pas être négative")
    .max(5, "La retenue de garantie ne peut pas dépasser 5 %")
    .default(5),
  notes: optional(1000),
});

export const removeSubcontractInvoiceInput = z.object({
  confirmationId: z.string().optional(),
  invoiceId: z.string().uuid("Identifiant de facture attendu"),
});

export const saveSubcontractPaymentInput = z.object({
  confirmationId: z.string().optional(),
  /** Absent à la création. */
  paymentId: z.string().uuid().optional(),
  subcontractId: z.string().uuid("Identifiant de marché attendu"),
  kind: z.enum(["invoice", "retention"]).default("invoice"),
  /** La facture réglée ; absente pour la retenue rendue. */
  invoiceId: uuidOrNull,
  amount: money("Le montant doit être positif"),
  paidOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "La date du règlement est requise"),
  /** Le client, sous délégation de paiement ou paiement direct. */
  paidBy: z.enum(["company", "client"]).default("company"),
  notes: optional(500),
});

export const removeSubcontractPaymentInput = z.object({
  confirmationId: z.string().optional(),
  paymentId: z.string().uuid("Identifiant de règlement attendu"),
});

export type SaveSubcontractInvoiceInput = z.infer<typeof saveSubcontractInvoiceInput>;
export type RemoveSubcontractInvoiceInput = z.infer<typeof removeSubcontractInvoiceInput>;
export type SaveSubcontractPaymentInput = z.infer<typeof saveSubcontractPaymentInput>;
export type RemoveSubcontractPaymentInput = z.infer<typeof removeSubcontractPaymentInput>;

// ─── Le planning partagé ───────────────────────────────────────────────────

export const openSubcontractPlanningInput = z.object({
  confirmationId: z.string().optional(),
  subcontractId: z.string().uuid("Identifiant de marché attendu"),
});

export const revokeSubcontractPlanningInput = z.object({
  confirmationId: z.string().optional(),
  linkId: z.string().uuid("Identifiant de lien attendu"),
});

export type OpenSubcontractPlanningInput = z.infer<typeof openSubcontractPlanningInput>;
export type RevokeSubcontractPlanningInput = z.infer<typeof revokeSubcontractPlanningInput>;
